/*
  ========================================================================
  File: assets/lm-product-dispatch.js - same-day dispatch countdown
  LAST UPDATED: 20/09/2026
  ========================================================================
  Turns the static dispatch sentence into a live countdown to the cutoff.
  Everything it needs comes from data attributes printed by
  snippets/lm-product-dispatch.liquid: the cutoff hour, the store timezone,
  optional holiday dates and the two sentences. Without JavaScript the
  static sentence stays. It never states a delivery date.
  ========================================================================
*/

if (!customElements.get('lm-dispatch-countdown')) {
  class LmDispatchCountdown extends HTMLElement {
    connectedCallback() {
      this.zone = this.dataset.timezone || 'Australia/Sydney';
      this.cutoff = Number(this.dataset.cutoffHour);
      this.holidays = new Set((this.dataset.holidays || '').split(',').map((d) => d.trim()).filter(Boolean));
      this.text = this.querySelector('[data-lm-dispatch-text]');
      if (!this.text || !Number.isFinite(this.cutoff)) return;

      this.formatter = new Intl.DateTimeFormat('en-AU', {
        timeZone: this.zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'short',
      });
      this.update();
      this.timer = window.setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    /** The current wall-clock time in the store's timezone. */
    now() {
      const parts = Object.fromEntries(this.formatter.formatToParts(new Date()).map((p) => [p.type, p.value]));
      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        minutes: Number(parts.hour) * 60 + Number(parts.minute) + Number(parts.second) / 60,
      };
    }

    /** A calendar date (UTC arithmetic only) is a dispatch day when it is a weekday and not listed as a holiday. */
    isBusinessDay(date) {
      const weekday = date.getUTCDay();
      if (weekday === 0 || weekday === 6) return false;
      return !this.holidays.has(date.toISOString().slice(0, 10));
    }

    update() {
      const now = this.now();
      const today = new Date(Date.UTC(now.year, now.month - 1, now.day));
      const cutoffMinutes = this.cutoff * 60;

      if (this.isBusinessDay(today) && now.minutes < cutoffMinutes) {
        const left = Math.ceil(cutoffMinutes - now.minutes);
        const hours = Math.floor(left / 60);
        const minutes = left % 60;
        const time = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        this.text.innerHTML = this.dataset.before.replace('{time}', `<span class="lm-product-dispatch__time">${time}</span>`);
        return;
      }

      const next = new Date(today);
      do {
        next.setUTCDate(next.getUTCDate() + 1);
      } while (!this.isBusinessDay(next));

      // Three-letter month ("Sep"): en-AU's own short month is "Sept".
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(next).map((p) => [p.type, p.value]));
      const label = `${parts.weekday} ${parts.day} ${parts.month.slice(0, 3)}`;
      this.text.textContent = this.dataset.after.replace('{day}', label);
    }
  }

  customElements.define('lm-dispatch-countdown', LmDispatchCountdown);
}
